/**
 * ShiftErrorHandler - Domain-specific error handling for shift operations
 */

import { HttpError } from '../../../core/services/HttpService';
import dialogUtil from '../../../shared/utils/dialog';

export class ShiftErrorHandler {
  private static instance: ShiftErrorHandler;

  static getInstance(): ShiftErrorHandler {
    if (!ShiftErrorHandler.instance) {
      ShiftErrorHandler.instance = new ShiftErrorHandler();
    }
    return ShiftErrorHandler.instance;
  }

  /**
   * Handle shift-specific HTTP errors
   * Returns true if error was handled, false if it should be passed to global handler
   */
  handle(error: HttpError): boolean {
    switch (error.status) {
      case 400:
        return this.handleBadRequest(error);
      
      case 409:
        return this.handleConflict(error);
      
      case 422:
        return this.handleValidationError(error);
      
      default:
        // Let global handler deal with common errors (401, 403, 500, etc.)
        return false;
    }
  }

  // Private error handling methods
  private handleBadRequest(error: HttpError): boolean {
    console.log('Shift bad request:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    dialogUtil.showToast({
      title: 'Invalid Shift Request',
      message: errorMessage,
      type: 'error',
    });
    
    return true; // Error was handled
  }

  private handleConflict(error: HttpError): boolean {
    console.log('Shift conflict:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    // Handle shift assignment conflicts specifically
    if (errorMessage.toLowerCase().includes('overlap')) {
      dialogUtil.showModal({
        title: 'Shift Assignment Conflict',
        content: errorMessage,
        primaryAction: {
          label: 'Remove Older Shift',
          onPress: () => {
            // This will be handled by the component with auto_remove_overlap flag
            // We just show the dialog here
          },
        },
        secondaryAction: {
          label: 'Cancel',
          onPress: () => {},
        },
        variant: 'warning',
        closeOnBackdropPress: false,
      });
    } else {
      dialogUtil.showToast({
        title: 'Shift Conflict',
        message: errorMessage,
        type: 'warning',
      });
    }
    
    return true; // Error was handled
  }

  private handleValidationError(error: HttpError): boolean {
    console.log('Shift validation error:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    dialogUtil.showToast({
      title: 'Shift Validation Error',
      message: errorMessage,
      type: 'error',
    });
    
    dialogUtil.showModal({
      title: 'Invalid Shift Data',
      content: errorMessage,
      primaryAction: {
        label: 'OK',
        onPress: () => {},
      },
      variant: 'error',
      closeOnBackdropPress: true,
    });
    
    return true; // Error was handled
  }

  /**
   * Extract meaningful error message from error response
   */
  private extractErrorMessage(error: HttpError): string {
    // Check for specific shift error fields
    if (error.data?.detail) {
      return error.data.detail;
    }
    
    if (error.data?.message) {
      return error.data.message;
    }
    
    // Check for field-specific validation errors
    if (error.data && typeof error.data === 'object') {
      const fields = Object.keys(error.data);
      if (fields.length > 0) {
        const fieldErrors = fields.map(field => {
          const fieldError = error.data[field];
          if (Array.isArray(fieldError)) {
            return `${field}: ${fieldError.join(', ')}`;
          }
          return `${field}: ${fieldError}`;
        }).join('; ');
        
        return fieldErrors;
      }
    }
    
    return 'A shift operation error occurred';
  }
}

// Export singleton instance
const shiftErrorHandler = ShiftErrorHandler.getInstance();
export default shiftErrorHandler; 